import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { exportToExcel, exportToPDF } from '../utils/exportUtils';
import { API_URL } from '../config';

interface DashboardProps {
  roadId: number;
  roadName: string;
}

interface CalculationResults {
  nombre_troncons: number;
  longueur_totale: number;
  surface_totale: number;
  note_visuelle_i1: number;
  note_structurelle_i5: number;
  note_chaussee: number;
  note_visuelle_i1_5ans: number;
  note_structurelle_i5_5ans: number;
  note_chaussee_5ans: number;
  repartition_trafic: Record<string, number>;
  diagnostic_surface: Record<string, Record<string, number>>;
  diagnostic_structurel: Record<string, Record<string, number>>;
  interventions_necessaires: Record<string, any[]>;
  evolution_data: Array<{year: number, i1: number, i5: number, i_chaussee: number}>;
}

const Dashboard: React.FC<DashboardProps> = ({ roadId, roadName }) => {
  const [results, setResults] = useState<CalculationResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const loadResults = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/roads/${roadId}/calculate`, {
        method: 'POST',
      });
      const data = await response.json();
      setResults(data);
    } catch (error) {
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roadId) {
      loadResults();
    }
  }, [roadId]);

  const getScoreColor = (score: number) => {
    if (score >= 4) return '#4CAF50';
    if (score >= 3) return '#FF9800';
    if (score >= 2) return '#FF5722';
    return '#F44336';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 4) return 'Excellent';
    if (score >= 3) return 'Bon';
    if (score >= 2) return 'Moyen';
    return 'Mauvais';
  };



  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Calcul en cours...</p>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="dashboard-empty">
        <p>Aucun résultat disponible</p>
        <button onClick={loadResults} className="btn-primary">
          Calculer les indices
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>📊 Dashboard - {roadName}</h2>
        <div className="header-actions">
          <button onClick={loadResults} className="btn-refresh">
            🔄 Actualiser
          </button>
          <button onClick={() => exportToExcel(roadId, roadName)} className="btn-excel">
            📊 Export Excel
          </button>
          <button onClick={() => exportToPDF(roadId, roadName)} className="btn-pdf">
            📄 Export PDF
          </button>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📈 Vue d'ensemble
        </button>
        <button 
          className={`tab ${activeTab === 'diagnostics' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagnostics')}
        >
          🔍 Diagnostics
        </button>
        <button 
          className={`tab ${activeTab === 'evolution' ? 'active' : ''}`}
          onClick={() => setActiveTab('evolution')}
        >
          📅 Évolution
        </button>
        <button 
          className={`tab ${activeTab === 'interventions' ? 'active' : ''}`}
          onClick={() => setActiveTab('interventions')}
        >
          🔧 Interventions
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="stats-section">
                <div className="stats-card">
                  <div className="card-header">
                    <h3>📊 Statistiques Générales</h3>
                  </div>
                  <div className="stats-content">
                    <div className="stat-row">
                      <span className="stat-label">Tronçons</span>
                      <span className="stat-value">{results.nombre_troncons}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Longueur</span>
                      <span className="stat-value">{results.longueur_totale?.toFixed(0) || '0'} m</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Surface</span>
                      <span className="stat-value">{results.surface_totale?.toFixed(0) || '0'} m²</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="scores-section">
                <div className="scores-card">
                  <div className="card-header">
                    <h3>🎯 Indices Actuels</h3>
                  </div>
                  <div className="scores-grid">
                    <div className="score-box">
                      <div className="score-label">I1 Visuel</div>
                      <div className="score-number" style={{ color: getScoreColor(results.note_visuelle_i1) }}>
                        {results.note_visuelle_i1?.toFixed(2) || '0.00'}
                      </div>
                      <div className="score-status">{getScoreLabel(results.note_visuelle_i1)}</div>
                    </div>
                    <div className="score-box">
                      <div className="score-label">I5 Structurel</div>
                      <div className="score-number" style={{ color: getScoreColor(results.note_structurelle_i5) }}>
                        {results.note_structurelle_i5?.toFixed(2) || '0.00'}
                      </div>
                      <div className="score-status">{getScoreLabel(results.note_structurelle_i5)}</div>
                    </div>
                    <div className="score-box main-score">
                      <div className="score-label">Note Chaussée</div>
                      <div className="score-number large" style={{ color: getScoreColor(results.note_chaussee) }}>
                        {results.note_chaussee?.toFixed(2) || '0.00'}
                      </div>
                      <div className="score-status">{getScoreLabel(results.note_chaussee)}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="projections-section">
                <div className="projections-card">
                  <div className="card-header">
                    <h3>🔮 Projections 5 ans</h3>
                  </div>
                  <div className="projections-grid">
                    <div className="projection-box">
                      <div className="projection-label">I1 (5 ans)</div>
                      <div className="projection-values">
                        <span className="projection-value" style={{ color: getScoreColor(results.note_visuelle_i1_5ans) }}>
                          {results.note_visuelle_i1_5ans?.toFixed(2) || '0.00'}
                        </span>
                        <span className={`projection-change ${((results.note_visuelle_i1_5ans || 0) - (results.note_visuelle_i1 || 0)) < 0 ? 'negative' : 'positive'}`}>
                          {((results.note_visuelle_i1_5ans || 0) - (results.note_visuelle_i1 || 0)) >= 0 ? '+' : ''}{((results.note_visuelle_i1_5ans || 0) - (results.note_visuelle_i1 || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="projection-box">
                      <div className="projection-label">I5 (5 ans)</div>
                      <div className="projection-values">
                        <span className="projection-value" style={{ color: getScoreColor(results.note_structurelle_i5_5ans) }}>
                          {results.note_structurelle_i5_5ans?.toFixed(2) || '0.00'}
                        </span>
                        <span className={`projection-change ${((results.note_structurelle_i5_5ans || 0) - (results.note_structurelle_i5 || 0)) < 0 ? 'negative' : 'positive'}`}>
                          {((results.note_structurelle_i5_5ans || 0) - (results.note_structurelle_i5 || 0)) >= 0 ? '+' : ''}{((results.note_structurelle_i5_5ans || 0) - (results.note_structurelle_i5 || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="projection-box">
                      <div className="projection-label">Chaussée (5 ans)</div>
                      <div className="projection-values">
                        <span className="projection-value" style={{ color: getScoreColor(results.note_chaussee_5ans) }}>
                          {results.note_chaussee_5ans?.toFixed(2) || '0.00'}
                        </span>
                        <span className={`projection-change ${((results.note_chaussee_5ans || 0) - (results.note_chaussee || 0)) < 0 ? 'negative' : 'positive'}`}>
                          {((results.note_chaussee_5ans || 0) - (results.note_chaussee || 0)) >= 0 ? '+' : ''}{((results.note_chaussee_5ans || 0) - (results.note_chaussee || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="traffic-distribution">
              <h3>🚗 Répartition du Trafic</h3>
              <div className="traffic-bars">
                {Object.entries(results.repartition_trafic || {}).map(([traffic, percent]) => (
                  <div key={traffic} className="traffic-bar">
                    <span className="traffic-label">{traffic}</span>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    <span className="traffic-percent">{percent?.toFixed(1) || '0.0'}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'diagnostics' && (
          <div className="diagnostics-tab">
            <div className="diagnostics-layout">
              <div className="diagnostic-section">
                <div className="section-header">
                  <h3>🔍 Diagnostic de Surface</h3>
                  <p>Répartition des conditions visuelles</p>
                </div>
                <div className="diagnostic-cards">
                  {Object.entries(results.diagnostic_surface || {}).map(([condition, data]) => (
                    <div key={condition} className="diagnostic-card">
                      <h4>{condition.replace('_', ' ')}</h4>
                      <div className="condition-breakdown">
                        {Object.entries(data || {}).map(([level, count]) => {
                          const percentage = results.nombre_troncons > 0 ? ((count as number / results.nombre_troncons) * 100) : 0;
                          return (
                            <div key={level} className={`condition-item ${level}`}>
                              <div className="condition-header">
                                <span className="condition-label">{level}</span>
                                <span className="condition-percentage">{percentage.toFixed(1)}%</span>
                              </div>
                              <div className="condition-bar">
                                <div 
                                  className={`bar-fill ${level}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="condition-count">{count} tronçon{count !== 1 ? 's' : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="diagnostic-section">
                <div className="section-header">
                  <h3>🏗️ Diagnostic Structurel</h3>
                  <p>Répartition par classe de trafic</p>
                </div>
                <div className="traffic-diagnostics">
                  {Object.entries(results.diagnostic_structurel || {}).map(([traffic, data]) => {
                    const totalSections = Object.values(data || {}).reduce((sum, count) => sum + (count as number), 0);
                    if (totalSections === 0) return null;
                    
                    return (
                      <div key={traffic} className="traffic-card">
                        <div className="traffic-header">
                          <h4>Classe {traffic}</h4>
                          <span className="traffic-total">{totalSections} tronçon{totalSections !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="traffic-breakdown">
                          {Object.entries(data || {}).map(([level, count]) => {
                            const percentage = totalSections > 0 ? ((count as number / totalSections) * 100) : 0;
                            return (
                              <div key={level} className={`traffic-item ${level}`}>
                                <div className="item-info">
                                  <span className="item-label">{level}</span>
                                  <span className="item-value">{count}</span>
                                </div>
                                <div className="item-bar">
                                  <div 
                                    className={`bar-fill ${level}`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="item-percentage">{percentage.toFixed(0)}%</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'evolution' && (
          <div className="evolution-tab">
            <div className="evolution-header">
              <h3>📈 Évolution des Indices sur 10 ans</h3>
              <p>Projection de la dégradation sans intervention</p>
            </div>
            {(results.evolution_data || []).length > 0 ? (
              <>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color i1"></div>
                    <span>Note Visuelle (I1)</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color i5"></div>
                    <span>Note Structurelle (I5)</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color chaussee"></div>
                    <span>Note Chaussée</span>
                  </div>
                </div>
                <div className="evolution-chart">
                  <div className="chart-y-axis">
                    <span>5.0</span>
                    <span>4.0</span>
                    <span>3.0</span>
                    <span>2.0</span>
                    <span>1.0</span>
                    <span>0.0</span>
                  </div>
                  <div className="chart-area">
                    <div className="chart-grid">
                      {[5, 4, 3, 2, 1].map(line => (
                        <div key={line} className="grid-line"></div>
                      ))}
                    </div>
                    <svg className="chart-svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
                      <polyline
                        className="line-i1"
                        points={results.evolution_data.map((p, i) => 
                          `${(i / 10) * 1000},${300 - (p.i1 / 5) * 300}`
                        ).join(' ')}
                      />
                      <polyline
                        className="line-i5"
                        points={results.evolution_data.map((p, i) => 
                          `${(i / 10) * 1000},${300 - (p.i5 / 5) * 300}`
                        ).join(' ')}
                      />
                      <polyline
                        className="line-chaussee"
                        points={results.evolution_data.map((p, i) => 
                          `${(i / 10) * 1000},${300 - (p.i_chaussee / 5) * 300}`
                        ).join(' ')}
                      />
                    </svg>
                    <div className="chart-points">
                      {results.evolution_data.map((point, index) => (
                        <div key={index} className="point-marker" style={{ left: `${(index / 10) * 100}%` }}>
                          <div className="point-tooltip">
                            <strong>Année {point.year}</strong>
                            <div>I1: {point.i1?.toFixed(2)}</div>
                            <div>I5: {point.i5?.toFixed(2)}</div>
                            <div>Chaussée: {point.i_chaussee?.toFixed(2)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="chart-x-axis">
                    {results.evolution_data.map((point, index) => (
                      <span key={index}>An {point.year}</span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="no-data">
                <p>📊 Aucune donnée d'évolution disponible</p>
                <p>Les données d'évolution seront générées après le calcul des indices.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'interventions' && (
          <div className="interventions-tab">
            {Object.entries(results.interventions_necessaires || {}).map(([category, interventions]) => (
              <div key={category} className="intervention-category">
                <h3 className={`category-title ${category}`}>
                  {category === 'immediate' && '🚨 Interventions Immédiates'}
                  {category === 'short_term' && '⚠️ Court Terme (1-2 ans)'}
                  {category === 'medium_term' && '📅 Moyen Terme (3-5 ans)'}
                  {category === 'long_term' && '📋 Long Terme (5+ ans)'}
                  <span className="category-count">({interventions.length})</span>
                </h3>
                
                {interventions.length === 0 ? (
                  <p className="no-interventions">Aucune intervention nécessaire</p>
                ) : (
                  <div className="interventions-list">
                    {(interventions || []).map((intervention, index) => (
                      <div key={index} className={`intervention-card ${category}`}>
                        <div className="intervention-header">
                          <h4>{intervention.section_name}</h4>
                          <span className={`priority priority-${intervention.priority}`}>
                            Priorité {intervention.priority}
                          </span>
                        </div>
                        <div className="intervention-details">
                          <div className="detail-item">
                            <span className="detail-label">Type:</span>
                            <span className="detail-value">{intervention.intervention_type}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Année:</span>
                            <span className="detail-value">{intervention.recommended_year}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Coût:</span>
                            <span className="detail-value cost">
                              {intervention.estimated_cost?.toLocaleString()} €
                            </span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Note:</span>
                            <span 
                              className="detail-value score"
                              style={{ color: getScoreColor(intervention.current_score) }}
                            >
                              {intervention.current_score} / 5
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;